import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../../utils/api";
import {
  destroy,
  fitBounds,
  initMap,
  setMarkers,
} from "../../planet/mapAdapter";

/** 活动地图壳：数据来自现有 /api/public/events，按城市打点 */
const CITY_FALLBACK = {
  合肥: [31.8206, 117.2272],
  上海: [31.2304, 121.4737],
  南昌: [28.682, 115.8579],
  重庆: [29.563, 106.5516],
  北京: [39.9042, 116.4074],
  杭州: [30.2741, 120.1551],
  广州: [23.1291, 113.2644],
  深圳: [22.5431, 114.0579],
  成都: [30.5728, 104.0668],
  武汉: [30.5928, 114.3055],
  南京: [32.0603, 118.7969],
};

function cityLatLng(city) {
  if (!city) return null;
  if (CITY_FALLBACK[city]) return CITY_FALLBACK[city];
  for (const [name, coords] of Object.entries(CITY_FALLBACK)) {
    if (city.includes(name) || name.includes(city)) return coords;
  }
  return null;
}

export default function PlanetEventsMapPage() {
  const mapRef = useRef(null);
  const adapterRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch("/api/public/events?limit=50");
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "加载活动失败");
        const list = Array.isArray(data) ? data : data.items || data.events || [];
        if (!cancelled) setEvents(list);
      } catch (e) {
        if (!cancelled) setMsg(e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    if (!adapterRef.current) {
      adapterRef.current = initMap(mapRef.current, { center: [31.2, 117.3], zoom: 5 });
    }
    const adapter = adapterRef.current;
    const markers = [];
    const pts = [];
    events.forEach((ev) => {
      const coords = cityLatLng(ev.city);
      if (!coords) return;
      const [lat, lng] = coords;
      pts.push({ lat, lng });
      markers.push({
        lat,
        lng,
        title: `${ev.title}${ev.city ? ` · ${ev.city}` : ""}`,
        onClick: () => setSelected(ev),
      });
    });
    setMarkers(adapter, markers);
    if (pts.length) fitBounds(adapter, pts);

    return () => {};
  }, [events]);

  useEffect(() => {
    return () => {
      if (adapterRef.current) {
        destroy(adapterRef.current);
        adapterRef.current = null;
      }
    };
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between gap-2 items-center">
        <p className="text-sm text-slate-600">
          复用站内交友活动数据，按城市在地图上浏览；报名与详情仍走原活动页。
        </p>
        <Link to="/events" className="text-sm text-teal-700 underline">
          列表视图
        </Link>
      </div>

      <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-100">
        <div ref={mapRef} style={{ height: 440, width: "100%" }} />
      </div>

      {msg && <p className="text-sm text-rose-600">{msg}</p>}

      {selected && (
        <div className="bg-white border rounded-xl p-4">
          <h3 className="font-medium text-teal-950">{selected.title}</h3>
          <p className="text-sm text-slate-500 mt-1">
            {[selected.city, selected.venue].filter(Boolean).join(" · ")}
          </p>
          {selected.summary && (
            <p className="text-sm text-slate-600 mt-2 line-clamp-3">{selected.summary}</p>
          )}
          <div className="mt-3 flex gap-2">
            <Link
              to={`/events/${selected.id}`}
              className="px-3 py-1.5 rounded-lg bg-teal-800 text-white text-sm"
            >
              查看详情
            </Link>
            <button
              type="button"
              className="px-3 py-1.5 rounded-lg border text-sm"
              onClick={() => setSelected(null)}
            >
              关闭
            </button>
          </div>
        </div>
      )}

      {!events.length && !msg && (
        <p className="text-sm text-slate-500 text-center py-6">暂无已发布活动</p>
      )}
    </div>
  );
}
